// askSmart-ui/src/pages/MapleCoin/MapleCoin.tsx
//
// Customer side of MapleCoin (/maplecoin): the signed-in customer's real
// MapleCoin account, read-only. Balances come from the MapleCoin ledger and
// activity from its transaction layer, through useMapleCoinAccount. There are
// deliberately no money-movement actions here.
import { useState } from 'react';
import { ArrowLeft, Landmark, RefreshCw, ShieldCheck } from 'lucide-react';
import { useMapleCoinAccount } from './useMapleCoinAccount';
import { AccountActivity, AccountOverview, Customer } from './types';
import { formatMoney, isZero } from './money';
import Flag from './components/Flag';
import AccountCard from './components/AccountCard';
import ActivityList from './components/ActivityList';
import './MapleCoin.css';

type View = { kind: 'home' } | { kind: 'account'; accountNumber: string };

const RECENT_LIMIT = 5;

const STATUS_NOTE: Record<AccountOverview['status'], string | null> = {
  OPEN: null,
  FROZEN: 'This account is frozen. Please contact MapleCoin support.',
  CLOSED: 'This account is closed.',
};

const RESTRICTED_NOTE = 'Some activity on this account is temporarily restricted. Please contact MapleCoin support.';

/**
 * The account's balances, exactly as the server reports them. The headline is
 * what can be used now; anything on hold is shown with the full balance, and
 * incoming Pending money is separate — it is never part of what can be used.
 */
function AccountPanel({ account }: { account: AccountOverview }) {
  const note = STATUS_NOTE[account.status] ?? (account.restricted ? RESTRICTED_NOTE : null);
  const held = account.heldMinor ?? '0';
  const spendable = account.spendableMinor ?? account.availableMinor;
  return (
    <section className="mc-balance" aria-label="MapleCoin account">
      <div className="mc-balance-label">
        <Flag currency={account.currency} size={22} />
        MapleCoin account
      </div>
      <div className="mc-balance-caption">Available balance</div>
      <div className="mc-balance-amount" data-testid="available-balance">
        {formatMoney(spendable, account.currency)}
      </div>
      {note && <div className="mc-account-status" role="status">{note}</div>}
      <dl className="mc-account-meta">
        <div>
          <dt>Account number</dt>
          <dd data-testid="account-number">{account.accountNumber}</dd>
        </div>
        {!isZero(held) && (
          <>
            <div>
              <dt>Balance</dt>
              <dd data-testid="ledger-balance">{formatMoney(account.availableMinor, account.currency)}</dd>
            </div>
            <div>
              <dt>On hold</dt>
              <dd data-testid="held-balance">{formatMoney(held, account.currency)}</dd>
            </div>
          </>
        )}
        <div>
          <dt>Pending</dt>
          <dd data-testid="pending-balance">{formatMoney(account.pendingMinor, account.currency)}</dd>
        </div>
      </dl>
    </section>
  );
}

function StatePanel({ icon: Icon, title, body, action }: {
  icon: typeof Landmark;
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <section className="mc-card mc-state" role="status">
      <span className="mc-empty-icon"><Icon size={22} /></span>
      <h1 className="mc-state-title">{title}</h1>
      <p>{body}</p>
      {action && (
        <button type="button" className="mc-link mc-state-action" onClick={action.onClick}>
          <RefreshCw size={16} /> {action.label}
        </button>
      )}
    </section>
  );
}

function Greeting({ customer }: { customer: Customer }) {
  return <p className="mc-greeting">{customer.displayName}</p>;
}

export default function MapleCoin() {
  const { state, reload, loadMore } = useMapleCoinAccount();
  const [view, setView] = useState<View>({ kind: 'home' });
  const home = () => setView({ kind: 'home' });

  let body;
  if (state.status === 'loading') {
    body = (
      <section className="mc-card mc-state" role="status" aria-busy="true">
        <p>Loading your MapleCoin account…</p>
      </section>
    );
  } else if (state.status === 'error') {
    body = (
      <StatePanel
        icon={Landmark}
        title="We can't show your account right now"
        body="Your money is safe. Please try again in a moment."
        action={{ label: 'Try again', onClick: reload }}
      />
    );
  } else if (state.status === 'not_onboarded') {
    body = (
      <StatePanel
        icon={ShieldCheck}
        title="Your MapleCoin account isn't set up yet"
        body="Once your MapleCoin account has been opened, your balance and transactions will appear here."
      />
    );
  } else if (state.accounts.length === 0) {
    body = (
      <>
        <Greeting customer={state.customer} />
        <StatePanel
          icon={Landmark}
          title="No accounts yet"
          body="Your MapleCoin account will appear here as soon as it has been opened."
        />
      </>
    );
  } else {
    const empty: AccountActivity = { transactions: [], nextBefore: null, loadingMore: false };
    const selected =
      view.kind === 'account' ? state.accounts.find((a) => a.accountNumber === view.accountNumber) : undefined;

    if (selected) {
      // One account in full: every transaction, with older pages on request.
      const activity = state.activity[selected.accountNumber] ?? empty;
      body = (
        <>
          <button type="button" className="mc-back" onClick={home}>
            <ArrowLeft size={18} /> Home
          </button>
          <AccountPanel account={selected} />
          <section className="mc-section">
            <h2 className="mc-section-title">Transactions</h2>
            <div className="mc-card">
              <ActivityList
                transactions={activity.transactions}
                onLoadMore={activity.nextBefore ? () => loadMore(selected.accountNumber) : undefined}
                loadingMore={activity.loadingMore}
              />
            </div>
          </section>
        </>
      );
    } else if (state.accounts.length === 1) {
      const account = state.accounts[0];
      const activity = state.activity[account.accountNumber] ?? empty;
      body = (
        <>
          <Greeting customer={state.customer} />
          <AccountPanel account={account} />
          <section className="mc-section">
            <h2 className="mc-section-title">Recent activity</h2>
            <div className="mc-card">
              <ActivityList
                transactions={activity.transactions}
                limit={RECENT_LIMIT}
                onViewAll={
                  activity.transactions.length > 0
                    ? () => setView({ kind: 'account', accountNumber: account.accountNumber })
                    : undefined
                }
              />
            </div>
          </section>
        </>
      );
    } else {
      body = (
        <>
          <Greeting customer={state.customer} />
          <section className="mc-section">
            <h2 className="mc-section-title">Your accounts</h2>
            <div className="mc-accounts">
              {state.accounts.map((a) => (
                <AccountCard
                  key={a.accountNumber}
                  account={a}
                  onOpen={() => setView({ kind: 'account', accountNumber: a.accountNumber })}
                />
              ))}
            </div>
          </section>
        </>
      );
    }
  }

  return (
    <div className="mc">
      <div className="mc-inner">{body}</div>
    </div>
  );
}
